import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { MassTortRowContextMenu } from "@/components/features/mass-torts/MassTortRowContextMenu";
import { retrieveMassTortSearchQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type MassTortSearch } from "@/lib/types";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { isNil } from "lodash";
import { useMemo, useState } from "react";

export type MassTortSearchTableRow = {
  rowId: string;
  /** Route param for `/mass-torts/:massTortSearchId` */
  massTortSearchId: string;
  title: string;
  description: string;
};

const MASS_TORTS_TABLE_COLUMN_IDS = ["Title"] as const;
const MASS_TORTS_TABLE_LS_KEY: string = tanstackTableNames.massTorts;

function mapMassTortSearchToRow(
  massTortSearch: MassTortSearch,
  index: number,
): MassTortSearchTableRow {
  const massTortSearchId = massTortSearch.id.trim();
  const rowId = massTortSearch.id || `mass-tort-search-${index}`;

  return {
    rowId,
    massTortSearchId,
    title: massTortSearch.title?.trim() ?? "",
    description: massTortSearch.description?.trim() ?? "",
  };
}

export interface MassTortsTableProps {
  tableButtons?: React.ReactNode;
  /** Constrains the table body to its parent and enables internal vertical scrolling. */
  scrollableBody?: boolean;
}

export function MassTortsTable({
  tableButtons,
  scrollableBody = false,
}: MassTortsTableProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY);
    return stored.sorting ?? [{ id: "Title", desc: false }];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY);
    const ids = [...MASS_TORTS_TABLE_COLUMN_IDS];
    const idSet = new Set<string>(ids);
    const order = stored.columnOrder;
    if (order?.length) {
      const normalized = order.filter((id) => idSet.has(id));
      return [...normalized, ...ids.filter((id) => !normalized.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY);
      return {
        ...stored.columnVisibility,
        Title: true,
      };
    },
  );

  const {
    data: massTortSearchResponse,
    isLoading,
    isFetching,
    isError,
  } = useQuery(retrieveMassTortSearchQueryOptions());

  const rows = useMemo(() => {
    const list = massTortSearchResponse?.data ?? [];
    return list.map(mapMassTortSearchToRow);
  }, [massTortSearchResponse?.data]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      return row.title.toLowerCase().includes(q);
    });
  }, [rows, searchQuery]);
  const columns = useMemo<ColumnDef<MassTortSearchTableRow>[]>(
    () => [
      {
        id: "Title",
        accessorFn: (row) => row.title,
        header: "Title",
        enableHiding: false,
        cell: ({ row }) => (
          <div className="max-w-md truncate font-medium">
            {row.original.title}
          </div>
        ),
        sortingFn: "alphanumeric",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY, { sorting: next });
        return next;
      });
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Title: true };
        persistTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: {
      customDateTime: (rowA, rowB, columnId) => {
        if (isNil(rowA.getValue(columnId))) {
          return 1;
        }
        if (isNil(rowB.getValue(columnId))) {
          return -1;
        }
        return dayjs(rowA.getValue(columnId)).diff(
          dayjs(rowB.getValue(columnId)),
        );
      },
    },
  });

  const sensors = useMattersStyleTableColumnDragSensors();
  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MASS_TORTS_TABLE_LS_KEY, { columnOrder: next });
      return next;
    });
  };

  return (
    <MattersStyleTableShell
      scrollableBody={scrollableBody}
      table={table}
      columnOrder={columnOrder}
      columnIdCountBaseline={MASS_TORTS_TABLE_COLUMN_IDS.length}
      onColumnDragEnd={handleColumnDragEnd}
      sensors={sensors}
      search={{
        value: searchQuery,
        onChange: (value) => {
          setSearchQuery(value);
          setPagination((p) => ({ ...p, pageIndex: 0 }));
        },
        placeholder: "Search mass torts...",
      }}
      toolbarTrailing={tableButtons}
      isLoading={isLoading || isFetching}
      isError={isError}
      errorMessage={serverErrorFallbackText}
      emptyMessage="No mass torts found."
      totalFilteredRowCount={filteredRows.length}
      entityNamePlural="mass torts"
      sorting={sorting}
      tableProps={{
        "data-testid": tanstackTableNames.massTorts,
        "data-table-name": tanstackTableNames.massTorts,
      }}
      getTableRowProps={(row) => ({
        "data-testid": "table-row",
        className: "cursor-pointer",
        onClick: () => {
          const id = row.original.massTortSearchId;
          if (!id) return;
          void navigate({ to: `/mass-torts/${id}` });
        },
      })}
      renderContextMenu={(row, trigger) => (
        <MassTortRowContextMenu
          massTortId={row.original.massTortSearchId}
          title={row.original.title}
          trigger={trigger}
        />
      )}
    />
  );
}
